import fullColorLogo from '@/assets/logo/full_color_rect.png';
import { Button } from '@/components/Button';
import { ColorPicker } from '@/components/ColorPicker';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useQrCode, useQrCodeStore } from '@/hooks/useQrCode';
import { useVCardGenerator } from '@/hooks/useVCardGenerator';
import { useAccessoryStore } from '@/stores/useAccessoryStore';
import { useCardStore } from '@/stores/useCardStore';
import { useUserStore } from '@/stores/useUserStore';
import i18n from '@/translations/config';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaQrcode } from 'react-icons/fa';
import { TbFileTypeSvg } from 'react-icons/tb';

const QR_CODE_COLORS = [
  { value: '#000000', key: 'black' },
  { value: '#A7F3D0', key: 'mint' },
  { value: '#34D399', key: 'emerald' },
  { value: '#2DD4BF', key: 'teal' },
  { value: '#22D3EE', key: 'cyan' },
  { value: '#38BDF8', key: 'sky' },
  { value: '#818CF8', key: 'indigo' },
  { value: '#A78BFA', key: 'violet' },
  { value: '#E879F9', key: 'fuchsia' },
  { value: '#FB7185', key: 'rose' },
  { value: '#FBBF24', key: 'amber' },
];

export const QRCodeGenerator = () => {
  const { t } = useTranslation('qrCodeGenerator');
  const { updateQrCodeOptions, downloadQrCode } = useQrCode();
  const qrCodeOptions = useQrCodeStore(state => state.qrCodeOptions);
  const { cards, listCards } = useCardStore();
  const { users, getUsers } = useUserStore();
  const { storeAccessory, isLoading, accessory } = useAccessoryStore();
  const [qrType, setQrType] = useState<'accessory' | 'url' | 'vcard'>(
    'accessory',
  );
  const [url, setUrl] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);

  // Find the selected card from the cards array
  const selectedCard = cards.find(card => card.id == selectedCardId);
  const { generateSimplifiedVCardContent } = useVCardGenerator(
    selectedCard || null,
  );

  useEffect(() => {
    listCards({
      paginate: 0,
    });

    getUsers();
  }, [listCards, getUsers]);

  const handleColorChange = (e: {
    target: { value: string; name: string };
  }) => {
    updateQrCodeOptions({ color: e.target.value });
    setRefreshKey(prev => prev + 1);
  };

  const handleGenerateQRCode = async () => {
    if (qrType === 'accessory') {
      const ownerId = selectedCard?.user_id ?? selectedUserId;

      //  Generate accessory QR code
      const accessoryResponse = await storeAccessory({
        owner_type: 'user',
        owner_id: ownerId ?? null,
        card_id: selectedCardId ?? null,
      });

      // Get the hostname from the current window location
      const hostname = window.location.origin;
      const accessoryUuid = accessoryResponse.data.uuid;
      const url = `${hostname}/fr/accessory/${accessoryUuid}`;
      toast.success(url);
      setQrValue(url);
    } else if (qrType === 'url' && url) {
      setQrValue(url);
    } else if (qrType === 'vcard' && selectedCard) {
      // Generate vCard with all links but no photo for QR code
      const vCardContent = await generateSimplifiedVCardContent();
      setQrValue(vCardContent);
    }
  };

  const handleDownload = () => {
    downloadQrCode(svgRef.current, 'linko-qrcode');
  };

  const handleDownloadCleanSvg = () => {
    if (!qrValue) return;

    // Create a new QR code as a React element
    const qrCodeElement = (
      <QRCodeSVG
        value={qrValue}
        size={300}
        bgColor={'#ffffff'}
        fgColor={qrCodeOptions.color || '#000000'}
        level="L"
      />
    );

    // Convert the QR code component to an SVG string
    const svgString = ReactDOMServer.renderToString(qrCodeElement);

    // Create a Blob from the SVG string
    const blob = new Blob([svgString], { type: 'image/svg+xml' });

    // Create a download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'linko-qrcode-clean.svg';
    document.body.appendChild(link);

    // Trigger download
    link.click();

    // Clean up
    document.body.removeChild(link);
  };

  const handleTypeChange = (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    setQrType(e.target.value as 'accessory' | 'url' | 'vcard');
    setQrValue('');
  };

  const handleCardChange = (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    setSelectedCardId(e.target.value as number);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 400,
        damping: 30,
      },
    },
  };

  const colorOptions = QR_CODE_COLORS.map(color => ({
    value: color.value,
    label: t(`colors.${color.key}`),
  }));

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="mx-auto max-w-7xl p-4 md:p-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-8 text-center">
        <div className="mb-4 flex items-center justify-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
            <FaQrcode className="size-8" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
          {t('title')}
        </h1>
        <p className="mt-2 text-lg text-gray-600">{t('subtitle')}</p>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* QR Code Generator Form */}
        <motion.div variants={itemVariants} className="space-y-6">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100 md:p-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FaQrcode className="size-5" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Configuration
              </h2>
            </div>

            <div className="space-y-6">
              <SelectInput
                label={t('form.qrType.label')}
                name="qrType"
                onChange={handleTypeChange}
                useFormik={false}
                containerStyle="w-full"
                options={[
                  {
                    label: t('form.qrType.options.accessory'),
                    value: 'accessory',
                  },
                  { label: t('form.qrType.options.url'), value: 'url' },
                  { label: t('form.qrType.options.vcard'), value: 'vcard' },
                ]}
                defaultValue={qrType}
              />

              {qrType === 'url' && (
                <TextInput
                  label={t('form.url.label')}
                  type="url"
                  placeholder={t('form.url.placeholder')}
                  onChange={(value: string) => setUrl(value)}
                  useFormik={false}
                  name="url"
                />
              )}

              {qrType === 'accessory' && (
                <div className="rounded-lg bg-blue-50 p-4">
                  <Text className="text-sm text-blue-700">
                    {t('instructions.accessory')}
                  </Text>
                </div>
              )}

              {qrType === 'url' && (
                <div className="rounded-lg bg-green-50 p-4">
                  <Text className="text-sm text-green-700">
                    {t('instructions.url')}
                  </Text>
                </div>
              )}

              {qrType === 'vcard' && (
                <div className="rounded-lg bg-purple-50 p-4">
                  <Text className="text-sm text-purple-700">
                    {t('instructions.vcard')}
                  </Text>
                </div>
              )}

              {qrType === 'accessory' && !selectedCardId && (
                <SelectInput
                  label={t('form.user.label')}
                  name="user"
                  defaultValue={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value as number)}
                  containerStyle="w-full"
                  useFormik={false}
                  options={users.map(user => ({
                    label: user.lastname + ' ' + user.firstname,
                    value: user.id,
                  }))}
                  isClearable
                />
              )}

              {(qrType === 'vcard' || qrType === 'accessory') &&
                !selectedUserId && (
                  <SelectInput
                    label={t('form.card.label')}
                    name="selectedCard"
                    defaultValue={selectedCardId}
                    onChange={handleCardChange}
                    containerStyle="w-full"
                    useFormik={false}
                    options={cards.map(card => ({
                      label: card.name,
                      value: card.id?.toString() || '',
                    }))}
                    isClearable
                  />
                )}

              <Button
                className="w-full"
                onClick={handleGenerateQRCode}
                disabled={
                  (qrType === 'accessory' && isLoading) ||
                  (qrType === 'url' && !url) ||
                  (qrType === 'vcard' && !selectedCardId)
                }
              >
                {qrType === 'accessory'
                  ? t('form.generate.accessory')
                  : t('form.generate.qrcode')}
              </Button>
            </div>
          </div>

          {qrValue && (
            <motion.div
              variants={itemVariants}
              className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100 md:p-8"
            >
              <div className="mb-6 flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
                  <FaQrcode className="size-5" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t('form.color.label')}
                </h3>
              </div>
              <ColorPicker
                name="qr_code_color"
                selectedColor={qrCodeOptions.color || '#000000'}
                onChange={handleColorChange}
                label=""
                colors={colorOptions}
              />
            </motion.div>
          )}
        </motion.div>

        {/* QR Code Preview */}
        <motion.div
          variants={itemVariants}
          className="rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
        >
          <div className="p-6 md:p-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <FaQrcode className="size-5" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                {t('preview.title')}
              </h3>
            </div>

            {qrType === 'accessory' && accessory?.uuid && (
              <div className="mb-6 overflow-hidden rounded-lg bg-gray-50 p-4">
                <Text className="break-all text-sm font-medium text-gray-600">
                  {window.location.origin}/{i18n.language}/accessory/
                  {accessory.uuid}
                </Text>
              </div>
            )}

            <div className="flex flex-col items-center justify-center">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="relative mb-8 flex size-80 max-w-full items-center justify-center rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 p-8"
              >
                {qrValue ? (
                  <QRCodeSVG
                    key={refreshKey}
                    value={qrValue}
                    size={280}
                    bgColor={'#ffffff'}
                    fgColor={qrCodeOptions.color || '#000000'}
                    level="L"
                    className="max-w-full rounded-lg drop-shadow-sm"
                    ref={svgRef}
                    imageSettings={{
                      src: fullColorLogo,
                      height: 56,
                      width: 56,
                      excavate: true,
                    }}
                  />
                ) : (
                  <div className="text-center">
                    <div className="mb-4 flex items-center justify-center">
                      <div className="flex size-16 items-center justify-center rounded-full bg-gray-200 text-gray-400">
                        <FaQrcode className="size-8" />
                      </div>
                    </div>
                    <p className="font-medium text-gray-900">
                      {t('preview.noPreview')}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      {t('preview.generateFirst')}
                    </p>
                  </div>
                )}
              </motion.div>

              {qrValue && (
                <motion.div
                  variants={itemVariants}
                  className="flex w-full flex-col gap-3 sm:flex-row"
                >
                  <Button onClick={handleDownload} className="flex-1">
                    <FaDownload className="mr-2 size-4" />
                    {qrType === 'vcard' && selectedCard
                      ? t('download.vcard')
                      : t('download.qrcode')}
                  </Button>
                  <Button onClick={handleDownloadCleanSvg} className="flex-1">
                    <TbFileTypeSvg className="mr-2 size-4" />
                    {t('download.svg')}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};
